import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const produtos = [
  { icon: 'ri-armchair-line', title: 'Cadeiras Ergonômicas', desc: 'Frisokar e Plaxmetal com certificação NR-17. Pronta entrega em Caruaru.', link: '/cadeiras' },
  { icon: 'ri-layout-grid-line', title: 'Mesas Corporativas', desc: 'Estações de trabalho e mesas de reunião Marzo Vitorino e Pandim.', link: '/mesas' },
  { icon: 'ri-layout-masonry-line', title: 'Divisórias Modulares', desc: 'Vidro, painel e acústica. Instalação sem obra em Caruaru.', link: '/divisorias' },
  { icon: 'ri-volume-mute-line', title: 'Cabines Acústicas', desc: 'Isolamento de até 45dB para open offices no Agreste.', link: '/cabines-acusticas' },
  { icon: 'ri-archive-line', title: 'Mobiliário de Aço', desc: 'Armários, estantes e lockers W3 Indústria.', link: '/aco' },
  { icon: 'ri-tools-line', title: 'Projetos Turnkey', desc: 'Planejamento, fornecimento e montagem completa do seu escritório.', link: '/turnkey' },
];

const diferenciais = [
  { icon: 'ri-store-2-line', title: 'Loja física em Caruaru', desc: 'Visite nossa loja na Av. Agamenon Magalhães, 1519. Veja os produtos pessoalmente antes de comprar.' },
  { icon: 'ri-tools-line', title: 'Montagem inclusa', desc: 'Nossa equipe instala tudo no seu escritório em Caruaru. Você não precisa se preocupar com nada.' },
  { icon: 'ri-time-line', title: 'Entrega rápida', desc: 'Pronta entrega para Caruaru e Agreste. Prazos diferenciados para clientes locais.' },
  { icon: 'ri-shield-check-line', title: 'Garantia e suporte local', desc: 'Assistência técnica em Caruaru. Garantia de até 5 anos nas cadeiras Frisokar e Plaxmetal.' },
];

const bairros = ['Centro', 'Maurício de Nassau', 'Rendeiras', 'Salgado', 'Vassoural', 'Petrópolis', 'Indianópolis', 'Luiz Gonzaga', 'Boa Vista', 'Manguinho', 'Riachão', 'Liberdade', 'Agamenon Magalhães', 'Universitário', 'Jardim Independência'];

export default function MoveisCorporativosCaruaruPage() {
  useSEO({
    title: 'Móveis Corporativos Caruaru PE | Cadeiras, Mesas e Divisórias — Home Office Premium',
    description: 'Móveis corporativos em Caruaru PE com loja física, entrega e montagem. Cadeiras ergonômicas NR-17, mesas corporativas, divisórias modulares e cabines acústicas. Revendedor Frisokar, Plaxmetal, Marzo Vitorino e W3 em Caruaru.',
    keywords: 'móveis corporativos Caruaru, cadeiras ergonômicas Caruaru, mesas corporativas Caruaru PE, divisórias modulares Caruaru, mobiliário escritório Caruaru, cadeiras escritório Caruaru, Frisokar Caruaru, Plaxmetal Caruaru, Marzo Vitorino Caruaru, W3 Caruaru, cabines acústicas Caruaru, armário aço Caruaru, móveis escritório Agreste, cadeiras NR17 Caruaru, projetos Turnkey Caruaru',
    path: '/moveis-corporativos-caruaru',
    ogTitle: 'Móveis Corporativos Caruaru PE | Home Office Premium',
    ogDescription: 'Móveis corporativos em Caruaru PE com loja física, entrega e montagem. Cadeiras ergonômicas, mesas, divisórias e cabines acústicas.',
  });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    const schemaId = 'schema-caruaru';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Home Office Premium — Móveis Corporativos Caruaru',
      description: 'Loja de móveis corporativos em Caruaru PE. Cadeiras ergonômicas, mesas corporativas, divisórias modulares e cabines acústicas.',
      url: `${siteUrl}/moveis-corporativos-caruaru`,
      telephone: '+55-81-98203-0285',
      email: 'ednilsonleocadio@gmail.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Av. Agamenon Magalhães, 1519',
        addressLocality: 'Caruaru',
        addressRegion: 'PE',
        postalCode: '55014-000',
        addressCountry: 'BR',
      },
      geo: { '@type': 'GeoCoordinates', latitude: -8.2760, longitude: -35.9753 },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '08:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '09:00', closes: '13:00' },
      ],
      areaServed: [
        { '@type': 'City', name: 'Caruaru' },
        { '@type': 'City', name: 'Bezerros' },
        { '@type': 'City', name: 'Gravatá' },
        { '@type': 'City', name: 'Santa Cruz do Capibaribe' },
        { '@type': 'City', name: 'Toritama' },
        { '@type': 'City', name: 'Belo Jardim' },
        { '@type': 'City', name: 'Pesqueira' },
      ],
      serviceType: 'Venda e Instalação de Móveis Corporativos',
    });

    const breadcrumbId = 'schema-caruaru-bc';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Área de Atendimento', item: `${siteUrl}/area-de-atendimento` },
        { '@type': 'ListItem', position: 3, name: 'Móveis Corporativos Caruaru', item: `${siteUrl}/moveis-corporativos-caruaru` },
      ],
    });

    const faqId = 'schema-caruaru-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'A Home Office Premium tem loja física em Caruaru?', acceptedAnswer: { '@type': 'Answer', text: 'Sim. Nossa loja fica na Av. Agamenon Magalhães, 1519, Caruaru PE. Atendemos de segunda a sexta das 8h às 18h e sábados das 9h às 13h.' } },
        { '@type': 'Question', name: 'Quais marcas de cadeiras ergonômicas vocês vendem em Caruaru?', acceptedAnswer: { '@type': 'Answer', text: 'Somos revendedores autorizados Frisokar e Plaxmetal em Caruaru. Ambas as marcas possuem certificação NR-17 e garantia de 5 anos.' } },
        { '@type': 'Question', name: 'Fazem projetos Turnkey de escritório em Caruaru?', acceptedAnswer: { '@type': 'Answer', text: 'Sim. Realizamos projetos Turnkey completos em Caruaru e Agreste: visita técnica, planejamento do layout, fornecimento e montagem de todos os móveis. Mais de 500 projetos entregues em Pernambuco.' } },
        { '@type': 'Question', name: 'Qual o endereço da Home Office Premium em Caruaru?', acceptedAnswer: { '@type': 'Answer', text: 'Av. Agamenon Magalhães, 1519, Caruaru PE. Telefone: (81) 98203-0285. Horário: seg-sex 8h-18h, sáb 9h-13h.' } },
      ],
    });

    return () => { script?.remove(); bcScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20">
        <div className="relative bg-[#111111] overflow-hidden">
          <div className="absolute inset-0">
            <img src="/media/ia/0391.webp" alt="Móveis corporativos em Caruaru PE" className="w-full h-full object-cover object-top opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-black/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-[#E8610A]/20 border border-[#E8610A]/40 text-[#E8610A] text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <i className="ri-map-pin-line"></i>
                Caruaru — Agreste de Pernambuco
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-5">
                Móveis Corporativos
                <span className="block text-[#E8610A]">em Caruaru</span>
              </h1>
              <p className="text-white/70 text-lg max-w-xl leading-relaxed mb-8">
                Loja física em Caruaru com cadeiras ergonômicas, mesas corporativas, divisórias e cabines acústicas. Revendedor autorizado Frisokar, Plaxmetal, Marzo Vitorino e W3 no Agreste de PE.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20de%20Caruaru%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-whatsapp-line"></i>Solicitar Orçamento
                </a>
                <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-store-2-line"></i>Visitar Loja
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Produtos disponíveis em Caruaru</h2>
              <p className="text-gray-500 mt-3 text-base max-w-lg mx-auto">Toda a linha de móveis corporativos com pronta entrega e montagem em Caruaru e Agreste.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {produtos.map(p => (
                <Link key={p.title} to={p.link} className="group bg-[#f8f8f6] rounded-2xl p-7 hover:bg-orange-50 transition-colors duration-300 cursor-pointer">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-5 group-hover:bg-[#E8610A] transition-colors duration-300">
                    <i className={`${p.icon} text-[#E8610A] text-xl group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-base mb-2">{p.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="py-20 bg-[#fafaf8]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Por que escolher</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Vantagens para Caruaru e Agreste</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {diferenciais.map(d => (
                <div key={d.title} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#E8610A]/10 rounded-xl mb-4">
                    <i className={`${d.icon} text-[#E8610A] text-xl`}></i>
                  </div>
                  <h3 className="text-gray-900 font-semibold text-base mb-2">{d.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Atendimento Local</p>
              <h2 className="text-3xl font-bold text-gray-900">Bairros de Caruaru que Atendemos</h2>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {bairros.map(b => (
                <span key={b} className="bg-[#f8f8f6] text-gray-700 text-sm font-medium px-4 py-2 rounded-full border border-gray-100">{b}</span>
              ))}
            </div>
            <p className="text-center text-gray-400 text-sm mt-6">E todos os demais bairros de Caruaru, além de Bezerros, Gravatá, Toritama, Santa Cruz do Capibaribe, Belo Jardim e Pesqueira.</p>
          </div>
        </div>

        <div className="py-16 bg-[#fafaf8]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-[#111111] rounded-3xl px-8 md:px-16 py-14 flex flex-col md:flex-row items-center justify-between gap-10 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#E8610A] rounded-l-3xl"></div>
              <div>
                <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Loja em Caruaru</p>
                <h2 className="text-3xl font-bold text-white mb-3">Visite nossa loja ou solicite orçamento</h2>
                <p className="text-white/60 text-base max-w-lg leading-relaxed">Av. Agamenon Magalhães, 1519 — Caruaru PE. Seg-Sex 8h-18h · Sáb 9h-13h</p>
              </div>
              <div className="flex flex-col gap-3 flex-shrink-0">
                <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20de%20Caruaru%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-whatsapp-line"></i>WhatsApp
                </a>
                <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-message-3-line"></i>Formulário de Contato
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20de%20Caruaru%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp">
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
